import React, { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";
import { motion } from "framer-motion";
import { Briefcase, Eye, Calendar, Users, User, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import AuthModal from "../components/modal/AuthModal";

const MyProjects = () => {
  const { user } = useContext(UserContext);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [applications, setApplications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  useEffect(() => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    const fetchMyProjects = async () => {
      try {
        const q = query(
          collection(db, "freelanceProjects"),
          where("userId", "==", user.id)
        );

        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const sortedData = data.sort((a, b) => {
          const timeA = a.createdAt?.seconds || 0;
          const timeB = b.createdAt?.seconds || 0;
          return timeB - timeA;
        });

        setProjects(sortedData);
      } catch (err) {
        console.error("Error fetching projects:", err);
        toast.error("Failed to load your projects.");
      } finally {
        setLoading(false);
      }
    };

    fetchMyProjects();
  }, [user]);

  const handleViewApplications = async (projectId) => {
    try {
      const q = query(
        collection(db, "projectApplications"),
        where("projectId", "==", projectId)
      );
      const snap = await getDocs(q);
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setApplications(data);
      setSelectedProject(projectId);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching applications:", err);
      toast.error("Failed to load applications.");
    }
  };

  const handleDeleteClick = (projectId) => {
    setProjectToDelete(projectId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    try {
      await deleteDoc(doc(db, "freelanceProjects", projectToDelete));
      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete));
      toast.success("Project deleted successfully!");
    } catch (err) {
      console.error("Error deleting project:", err);
      toast.error("Failed to delete project.");
    } finally {
      setShowDeleteModal(false);
      setProjectToDelete(null);
    }
  };

  return (
    <Wrapper>
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        My <Highlight>Freelance Projects</Highlight>
      </motion.h1>

      <Subtitle>Manage your posted projects and view applicants.</Subtitle>

      {loading ? (
        <Loading>Loading your projects...</Loading>
      ) : projects.length === 0 ? (
        <Empty>You haven’t posted any projects yet 🚀</Empty>
      ) : (
        <ProjectsGrid>
          {projects.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <ProjectCard>
                <Header>
                  <Briefcase size={20} color="#c59fff" />
                  <h3>{p.title}</h3>
                </Header>

                <Small>
                  <Calendar size={14} />{" "}
                  {p.createdAt?.seconds
                    ? new Date(p.createdAt.seconds * 1000).toLocaleDateString()
                    : "Recently"}
                </Small>

                <p>
                  {p.description?.slice(0, 100) || "No description available."}
                </p>

                <Footer>
                  <ViewButton onClick={() => handleViewApplications(p.id)}>
                    <Eye size={14} /> View Applications
                  </ViewButton>
                  <DeleteButton onClick={() => handleDeleteClick(p.id)}>
                    <Trash2 size={14} /> Delete
                  </DeleteButton>
                </Footer>
              </ProjectCard>
            </motion.div>
          ))}
        </ProjectsGrid>
      )}

      {/* ---------------- Applications Modal ---------------- */}
      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <h3>
                <Users size={20} /> Applicants ({applications.length})
              </h3>
              <CloseBtn onClick={() => setShowModal(false)}>×</CloseBtn>
            </ModalHeader>

            {applications.length === 0 ? (
              <Empty>No applications yet.</Empty>
            ) : (
              <AppList>
                {applications.map((a) => (
                  <AppCard key={a.id}>
                    <AppLeft>
                      {a.userImage ? (
                        <Avatar src={a.userImage} />
                      ) : (
                        <User size={20} color="#c59fff" />
                      )}
                      <div>
                        <h4>{a.userName}</h4>
                        <small>
                          {a.createdAt?.seconds
                            ? new Date(
                                a.createdAt.seconds * 1000
                              ).toLocaleDateString()
                            : "Recently"}
                        </small>
                      </div>
                    </AppLeft>

                    <ProfileButton
                      onClick={() => {
                        if (user && user.id === a.userId)
                          window.open(`/profile/${user.id}`);
                        else window.open(`/developers/${a.userId}`);
                      }}
                    >
                      View Profile
                    </ProfileButton>
                  </AppCard>
                ))}
              </AppList>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* ---------------- Delete Confirmation Modal ---------------- */}
      {showDeleteModal && (
        <ModalOverlay>
          <ConfirmModal>
            <h3>Are you sure you want to delete this project?</h3>
            <p>This action cannot be undone.</p>
            <ConfirmButtons>
              <CancelBtn onClick={() => setShowDeleteModal(false)}>
                Cancel
              </CancelBtn>
              <ConfirmBtn onClick={confirmDelete}>Yes, Delete</ConfirmBtn>
            </ConfirmButtons>
          </ConfirmModal>
        </ModalOverlay>
      )}
    </Wrapper>
  );
};

export default MyProjects;

/* ---------- Styles ---------- */
const Wrapper = styled.section`
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 100px 20px;
  text-align: center;
  min-height: 100vh;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  color: #d2cde9;
  font-size: 1.1rem;
  margin-top: 10px;
  margin-bottom: 40px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Small = styled.p`
  font-size: 0.8rem;
  color: #aaa;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
`;

const Footer = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 10px;
`;

const ViewButton = styled.button`
  background: transparent;
  border: 1px solid #9b5cff;
  color: #c59fff;
  border-radius: 8px;
  padding: 6px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover {
    background: #9b5cff;
    color: white;
  }
`;

const DeleteButton = styled(ViewButton)`
  border-color: #ff4d4d;
  color: #ff8080;
  &:hover {
    background: #ff4d4d;
    color: white;
  }
`;

const Loading = styled.p`
  color: #c59fff;
`;

const Empty = styled.p`
  color: #aaa;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1c1b22;
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 30px;
  width: 90%;
  max-width: 600px;
  text-align: left;
  color: #fff;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h3 {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 1.5rem;
  cursor: pointer;
`;

const AppList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AppCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AppLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
`;

const ProfileButton = styled(ViewButton)`
  padding: 6px 10px;
  font-size: 0.85rem;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const ProjectCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  text-align: left;
  transition: 0.3s;
  backdrop-filter: blur(8px);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 230px;

  &:hover {
    transform: translateY(-5px);
    border-color: #c59fff;
    box-shadow: 0 0 20px rgba(155, 92, 255, 0.3);
  }
`;

/* --- Delete Confirmation Modal Styles --- */
const ConfirmModal = styled.div`
  background: #1c1b22;
  border: 1px solid rgba(255, 77, 77, 0.4);
  border-radius: 16px;
  padding: 30px;
  width: 90%;
  max-width: 400px;
  text-align: center;
  color: #fff;

  h3 {
    margin-bottom: 10px;
  }
  p {
    color: #ccc;
    margin-bottom: 20px;
  }
`;

const ConfirmButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
`;

const CancelBtn = styled.button`
  background: transparent;
  border: 1px solid #666;
  color: #ccc;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  &:hover {
    background: #333;
  }
`;

const ConfirmBtn = styled.button`
  background: #ff4d4d;
  border: none;
  color: white;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    background: #ff6666;
  }
`;
